/**
 * Supervisor agent settings — extracted from SettingsPage into a self-contained
 * card with its own debounced autosave. Pro-gated via workspace.has_autopilot.
 */
import React, { useEffect, useState } from 'react';
import { Card, FormField, Input, useToast } from '../ui';
import type { Workspace } from '../../types';
import {
  ToggleRow,
  SaveStatusPill,
  useWorkspaceAutosave,
  utcHourToBrowserLocalHour,
  browserLocalHourToUtcHour,
} from './agentsCommon';

interface Props {
  workspace: Workspace;
  onWorkspaceUpdate: (_ws: Workspace) => void;
}

export default function SupervisorSettings({ workspace, onWorkspaceUpdate }: Props) {
  const { toast } = useToast();
  const canAutopilot = workspace.has_autopilot ?? false;
  const { saveStatus, queueSave } = useWorkspaceAutosave(
    workspace.id,
    onWorkspaceUpdate,
    (msg) => toast(msg, 'error')
  );

  const [enabled, setEnabled] = useState(workspace.supervisor_enabled ?? false);
  const [hour, setHour] = useState(
    String(utcHourToBrowserLocalHour(workspace.supervisor_hour ?? 9))
  );
  const [intervalHours, setIntervalHours] = useState(
    String(workspace.supervisor_interval_hours ?? 24)
  );
  const [minOutlines, setMinOutlines] = useState(
    String(workspace.supervisor_min_outlines ?? 7)
  );

  useEffect(() => {
    setEnabled(workspace.supervisor_enabled ?? false);
    setHour(String(utcHourToBrowserLocalHour(workspace.supervisor_hour ?? 9)));
    setIntervalHours(String(workspace.supervisor_interval_hours ?? 24));
    setMinOutlines(String(workspace.supervisor_min_outlines ?? 7));
  }, [workspace]);

  return (
    <Card elevation="raised">
      <Card.Header>
        <Card.Title subtitle="Configure the autonomous supervisor that keeps your content pipeline healthy.">
          Settings
          {!canAutopilot && <span className="settings-plan-lock-badge">🔒 Pro</span>}
        </Card.Title>
      </Card.Header>
      <Card.Body className="settings-card-body">
        {!canAutopilot && (
          <div className="settings-entitlement-notice">
            The Supervisor Agent is available on the <strong>Pro plan</strong>. Upgrade to let
            Conistra run your account end-to-end.
          </div>
        )}
        <ToggleRow
          label="Enable Supervisor"
          description={
            canAutopilot
              ? 'The supervisor wakes up on a schedule, checks your content pipeline, and creates new outlines when needed.'
              : '🔒 Upgrade to Pro to enable the Supervisor Agent.'
          }
          checked={enabled && canAutopilot}
          onChange={(v) => {
            if (!canAutopilot) return;
            setEnabled(v);
            queueSave({ supervisor_enabled: v });
          }}
          disabled={!canAutopilot}
        />

        {enabled && canAutopilot && (
          <div className="settings-number-row">
            <FormField
              label="Run at Hour"
              htmlFor="sv-hour"
              hint="Hour of day in your browser's local time (0 = midnight, 9 = 9:00 AM)."
            >
              <Input
                id="sv-hour"
                type="number"
                min={0}
                max={23}
                value={hour}
                onChange={(e) => {
                  setHour(e.target.value);
                  const n = parseInt(e.target.value, 10);
                  if (!isNaN(n)) queueSave({ supervisor_hour: browserLocalHourToUtcHour(n) });
                }}
              />
            </FormField>
            <FormField label="Interval (hours)" htmlFor="sv-interval" hint="How many hours between runs. 24 = daily.">
              <Input
                id="sv-interval"
                type="number"
                min={1}
                max={168}
                value={intervalHours}
                onChange={(e) => {
                  setIntervalHours(e.target.value);
                  const n = parseInt(e.target.value, 10);
                  if (!isNaN(n) && n >= 1) queueSave({ supervisor_interval_hours: n });
                }}
              />
            </FormField>
            <FormField label="Min Outlines" htmlFor="sv-min" hint="Minimum scheduled outlines the supervisor maintains.">
              <Input
                id="sv-min"
                type="number"
                min={1}
                max={100}
                value={minOutlines}
                onChange={(e) => {
                  setMinOutlines(e.target.value);
                  const n = parseInt(e.target.value, 10);
                  if (!isNaN(n) && n >= 1) queueSave({ supervisor_min_outlines: n });
                }}
              />
            </FormField>
          </div>
        )}
        <div style={{ marginTop: 'var(--space-3)' }}>
          <SaveStatusPill status={saveStatus} />
        </div>
      </Card.Body>
    </Card>
  );
}
